import {EmailLog, OutputStatus} from "../types";
import {ImagesByStation} from "./images";

/**
 * Formato "grezzo" del file output.json prodotto dal backend.
 * Supporta il nuovo formato per timelapse (prodotti/base_path/timestamp_inizio...)
 * e, come fallback, il vecchio formato piatto (timestamp/lista_path_immagini/path_txt).
 */
export type RawOutput = {
    // nuovo formato
    timelapse_id?: string;
    giorno?: string;
    timestamp_inizio?: string;
    timestamp_fine?: string;
    base_path?: string;
    csv?: string[];
    prodotti?: Record<string, string[]>;
    // comune / vecchio formato
    timestamp?: string;
    stima_radar?: number | null;
    stima_sensori?: number | null;
    media_stima?: number | null;
    lista_path_immagini?: ImagesByStation | string[];
    path_txt?: string;
    email?: EmailLog | null;
};

/** Directory (con slash finale) del file a partire dal suo URL. */
const dirOf = (url: string): string => {
    const i = (url || "").lastIndexOf("/");
    return i >= 0 ? url.slice(0, i + 1) : "";
};

/**
 * Converte il JSON grezzo nel modello interno usato dal frontend.
 * I nomi file in `prodotti`/`csv` sono relativi alla cartella del timelapse:
 * vengono risolti rispetto alla directory dell'output.json di provenienza.
 */
export const parseOutputJson = (raw: RawOutput | null | undefined, sourceUrl: string): OutputStatus | null => {
    if (!raw) {
        return null;
    }

    const dir = dirOf(sourceUrl);
    const resolve = (name: string): string => {
        const n = (name || "").trim();
        if (!n) {
            return "";
        }
        if (n.startsWith("http://") || n.startsWith("https://") || n.startsWith("/")) {
            return n;
        }
        return `${dir}${n}`;
    };

    // Nuovo formato (timelapse)
    if (raw.prodotti || raw.timestamp_inizio) {
        const imagesByStation: ImagesByStation = {};
        for (const [station, files] of Object.entries(raw.prodotti ?? {})) {
            if (!Array.isArray(files)) {
                continue;
            }
            const list = files
                .filter((f) => typeof f === "string" && f.trim().length > 0)
                .map((f) => resolve(f));
            if (list.length) {
                imagesByStation[station] = list;
            }
        }

        const inizio = raw.timestamp_inizio ?? raw.timestamp ?? "";
        const fine = raw.timestamp_fine ?? "";
        const csvName = Array.isArray(raw.csv)
            ? raw.csv.find((c) => typeof c === "string" && c.trim().length > 0)
            : undefined;

        return {
            timestamp: fine ? `${inizio} → ${fine}` : inizio,
            stima_radar: raw.stima_radar ?? null,
            stima_sensori: raw.stima_sensori ?? null,
            media_stima: raw.media_stima ?? null,
            lista_path_immagini: imagesByStation,
            path_txt: csvName ? resolve(csvName) : "",
            email: raw.email ?? null,
        };
    }

    // Vecchio formato (fallback)
    if (typeof raw.timestamp === "string") {
        return {
            timestamp: raw.timestamp,
            stima_radar: raw.stima_radar ?? null,
            stima_sensori: raw.stima_sensori ?? null,
            media_stima: raw.media_stima ?? null,
            lista_path_immagini: raw.lista_path_immagini ?? {},
            path_txt: raw.path_txt ?? "",
            email: raw.email ?? null,
        };
    }

    return null;
};
